/**
 * จอรวมสำหรับ present — โหลด **iframe 6 อันของ apps/screen ตัวเดียวกับที่ขึ้นจอจริง**
 *
 * ห้ามเขียน renderer ซ้ำแม้บรรทัดเดียว (CLAUDE.md กฎเหล็กข้อ 4)
 * ถ้า fork สิ่งที่ลูกค้าเห็นจะไม่ใช่สิ่งที่ขึ้นจอจริง แล้วการ present ก็ไม่มีความหมาย
 *
 * ไฟล์นี้จึงมีแค่ layout + ปุ่มสั่งงาน ไม่มีโค้ดวาดฉากหรือตัวละครเลย
 */
import { startAutoReload } from "@py/net";
import { SCENE_IDS, SCENE_NAME } from "@py/protocol";

import { ENV } from "./env.js";

// เปลือก present แทบไม่เปลี่ยน แต่ iframe ข้างในเช็คเองไม่ได้ทั่วถึงเท่า reload ทั้งหน้า
// จอนี้ไม่มีผู้เล่นผูกกับตัวเปลือกโดยตรง จึง reload ได้ทันทีที่เจอของใหม่
declare const __BUILD__: string;
startAutoReload(__BUILD__, () => false);

const wall = document.getElementById("wall")!;
const params = new URLSearchParams(location.search);
/** เครื่องหน้างานอืด → ให้จอโหลดฉากรุ่นเบา (ส่งต่อไปให้ apps/screen ตัดสินใจ) */
const light = params.get("light") === "1";

/**
 * ทยอยเปิด iframe ทีละอัน ไม่เปิดพร้อมกันทั้ง 6
 *
 * แต่ละอันสร้าง Phaser + decode ภาพฉาก 1080×1920 ทันทีที่โหลด ถ้าปล่อยพร้อมกัน
 * ทั้ง 6 จะแย่ง CPU/หน่วยความจำในจังหวะเดียว บางจออาจวาดไม่ทันหรือค้างดำ
 * (เจอตอนทดสอบ: ช่องที่ขึ้นภาพไม่ซ้ำกันในแต่ละรอบ)
 * ห่างกัน 400 ms รวมแล้วยังเปิดครบใน 2 วิ ซึ่งเร็วพอสำหรับหน้า present
 */
const STAGGER_MS = 400;

const frames: HTMLIFrameElement[] = [];

for (const s of SCENE_IDS) {
  const cell = document.createElement("div");
  cell.className = "cell";

  const frame = document.createElement("iframe");
  const q = new URLSearchParams({ scene: String(s), present: "1" });
  if (light) q.set("light", "1");
  frame.dataset.src = `${ENV.screenBase}/?${q.toString()}`;
  frame.title = `จอ ${s} ${SCENE_NAME[s]}`;
  // อนุญาตเฉพาะที่จำเป็น — จอ present ไม่ต้องใช้กล้องหรือไมค์
  frame.setAttribute("allow", "autoplay");

  /**
   * ป้ายชื่อฐานเป็นลิงก์เปิดจอนั้นในแท็บใหม่
   *
   * ลิงก์ไปที่ URL ของ **จอจริง** คือไม่มี `present=1` — หน้า present ตั้งค่านั้น
   * เพื่อไม่ให้ iframe ประกาศ `screen/{n}/status` (ไม่งั้นสถานะจอจะกระพริบ
   * ตอนเปิดพร้อมจอจริง ดู M5.2) แต่แท็บที่เปิดใหม่คือจอจริงเต็มตัว จึงต้องไม่มี
   */
  const tag = document.createElement("a");
  tag.className = "tag";
  const openQ = new URLSearchParams({ scene: String(s) });
  if (light) openQ.set("light", "1");
  tag.href = `${ENV.screenBase}/?${openQ.toString()}`;
  tag.target = "_blank";
  tag.rel = "noopener";
  tag.title = `เปิดจอ ${s} ${SCENE_NAME[s]} ในแท็บใหม่`;
  tag.textContent = `${s} ${SCENE_NAME[s]} ↗`;

  cell.append(frame, tag);
  wall.appendChild(cell);
  frames.push(frame);
}

frames.forEach((f, i) => {
  setTimeout(() => {
    f.src = f.dataset.src!;
  }, i * STAGGER_MS);
});

/**
 * ปุ่มสั่งงานส่งผ่าน sys/cmd ที่ broker — จอทุกอันรวมถึงจอจริงจะได้รับพร้อมกัน
 * (จอ present ไม่ได้ต่อ MQTT เอง จึงส่งผ่าน iframe ที่ต่ออยู่แล้ว ไม่ต้องมี
 *  credential ซ้ำอีกชุดในหน้านี้)
 */
/**
 * "reload ทุกจอ" ต้องเป็น **hard refresh**: ตั้ง src ใหม่พร้อม cache-buster
 * ไม่ใช้ postMessage reload — `location.reload()` ใน iframe อาจได้ HTML เก่า
 * จากแคชของเบราว์เซอร์ แล้วก็วนกลับมาเป็นโค้ดชุดเดิมอยู่ดี
 * (`?r=` ทำให้ document สดแน่นอน ส่วน asset มี hash ในชื่อไฟล์อยู่แล้ว)
 * ทยอยทีละจอเหมือนตอนเปิดหน้า — reload พร้อมกัน 6 อันแล้วบางช่องค้างดำ
 */
function hardReloadAll(): void {
  frames.forEach((f, i) => {
    setTimeout(() => {
      const u = new URL(f.dataset.src!);
      u.searchParams.set("r", String(Date.now()));
      f.src = u.toString();
    }, i * STAGGER_MS);
  });
}

for (const btn of document.querySelectorAll<HTMLButtonElement>("#bar button[data-cmd]")) {
  btn.addEventListener("click", () => {
    const cmd = btn.dataset.cmd!;
    if (cmd === "reload") {
      hardReloadAll();
      return;
    }
    for (const f of wall.querySelectorAll("iframe")) {
      f.contentWindow?.postMessage({ py: "sys", cmd }, "*");
    }
  });
}

document.getElementById("rows")!.addEventListener("click", (e) => {
  // 2 แถว × 3 ได้จอใหญ่ขึ้นเล็กน้อยและยังเห็นครบ 6 — เลือกได้ตามจอที่ใช้จริง
  wall.classList.toggle("rows2");
  (e.currentTarget as HTMLButtonElement).textContent =
    wall.classList.contains("rows2") ? "1 แถว" : "2 แถว";
});

document.getElementById("full")!.addEventListener("click", () => {
  if (document.fullscreenElement) void document.exitFullscreen();
  else void document.documentElement.requestFullscreen();
});
